import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createRole,
  deleteRole,
  getRoles,
  getRolesForOps,
  getUserRoles,
  setUserRoles,
  updateRole,
} from "../services/role-service";
import { CreateRolePayload, UpdateRolePayload } from "../types";

export const roleKeys = {
  all: ["roles"] as const,
  lists: () => [...roleKeys.all, "list"] as const,
  list: (ops: string) => [...roleKeys.lists(), { ops }] as const,
  listForOps: (opsList: string[]) =>
    [...roleKeys.lists(), { opsList: [...opsList].sort() }] as const,
  userRoles: (userId: string) => [...roleKeys.all, "user", userId] as const,
};

export const useRoles = (ops?: string) =>
  useQuery({
    queryKey: roleKeys.list(ops ?? ""),
    queryFn: () => getRoles(ops as string),
    enabled: !!ops,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 1,
  });

export const useRolesForOps = (opsList: string[]) =>
  useQuery({
    queryKey: roleKeys.listForOps(opsList),
    queryFn: () => getRolesForOps(opsList),
    enabled: opsList.length > 0,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

export const useCreateRole = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateRolePayload) => createRole(payload),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: roleKeys.lists() }),
  });
};

export const useUpdateRole = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateRolePayload }) =>
      updateRole(id, payload),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: roleKeys.lists() }),
  });
};

export const useDeleteRole = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteRole(id),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: roleKeys.lists() }),
  });
};

export const useUserRoles = (userId?: string) =>
  useQuery({
    queryKey: roleKeys.userRoles(userId ?? ""),
    queryFn: () => getUserRoles(userId as string),
    enabled: !!userId,
    staleTime: 0,
  });

export const useSetUserRoles = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, roleIds }: { userId: string; roleIds: string[] }) =>
      setUserRoles(userId, roleIds),
    onSuccess: (_data, { userId }) =>
      queryClient.invalidateQueries({ queryKey: roleKeys.userRoles(userId) }),
  });
};
